import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { Close, Delete, Sync, Undo } from '@material-symbols-svg/react/rounded/w500';
import { memo } from 'react';

import { formatHM } from '../../common';
import { MONO_FONT, palette, tokens } from '../../theme';

export type EntryCardStatus = 'saved' | 'pendingAdd' | 'pendingRemove';

export type EntryCardProps = {
    idLabel: string;
    title: string;
    hours: number;
    accent: string;
    typeLabel: string;
    status: EntryCardStatus;
    onRemove?: () => void;
    removeAriaLabel?: string;
};

export const EntryCard = memo(function EntryCard({
    idLabel,
    title,
    hours,
    accent,
    typeLabel,
    status,
    onRemove,
    removeAriaLabel,
}: EntryCardProps) {
    const classes = useStyles();
    const isPendingRemove = status === 'pendingRemove';

    return (
        <div className={mergeClasses(classes.row, status === 'pendingAdd' && classes.pendingAdd, isPendingRemove && classes.pendingRemove)}>
            <span className={classes.dot} style={{ backgroundColor: accent }} aria-label={typeLabel} />
            <span className={classes.idText}>{idLabel}</span>
            <span className={classes.title}>{title}</span>
            {status === 'pendingAdd' ? (
                <Sync className={classes.pendingIcon} aria-label="Pending add" />
            ) : isPendingRemove ? (
                <Delete className={classes.pendingRemoveIcon} aria-label="Pending removal" />
            ) : (
                <span className={classes.iconPlaceholder} aria-hidden />
            )}
            <span className={mergeClasses(classes.hoursText, isPendingRemove && classes.hoursStruck)}>{formatHM(hours)}</span>
            <button
                type="button"
                className={mergeClasses(classes.removeButton, !onRemove && classes.removeDisabled)}
                aria-label={removeAriaLabel ?? 'Remove entry'}
                disabled={!onRemove}
                onClick={() => onRemove?.()}
            >
                {isPendingRemove ? (
                    <Undo className={classes.removeIcon} aria-hidden />
                ) : (
                    <Close className={classes.removeIcon} aria-hidden />
                )}
            </button>
        </div>
    );
});

const useStyles = makeStyles({
    row: {
        display: 'grid',
        gridTemplateColumns: '8px 64px minmax(0, 1fr) auto auto auto',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: '12px 14px',
        backgroundColor: palette.panelBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: tokens.borderRadiusMedium,
        marginBottom: '8px',
        '&:hover': {
            border: `1px solid ${palette.outlineGray}`,
        },
    },
    pendingAdd: {
        border: `1px solid ${palette.cardBorder}`,
        borderLeft: `3px solid ${palette.pendingAccent}`,
        paddingLeft: '12px',
        '&:hover': {
            border: `1px solid ${palette.outlineGray}`,
            borderLeft: `3px solid ${palette.pendingAccent}`,
        },
    },
    pendingRemove: {
        border: `1px solid ${palette.cardBorder}`,
        borderLeft: `3px solid ${palette.overText}`,
        paddingLeft: '12px',
        '&:hover': {
            border: `1px solid ${palette.outlineGray}`,
            borderLeft: `3px solid ${palette.overText}`,
        },
    },
    dot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    idText: {
        fontFamily: MONO_FONT,
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
    },
    title: {
        fontSize: '13px',
        color: tokens.colorNeutralForeground1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
    },
    pendingIcon: {
        width: '14px',
        height: '14px',
        color: palette.pendingAccent,
        flexShrink: 0,
    },
    pendingRemoveIcon: {
        width: '14px',
        height: '14px',
        color: palette.overText,
        flexShrink: 0,
    },
    iconPlaceholder: {
        width: '14px',
        height: '14px',
        flexShrink: 0,
    },
    hoursText: {
        fontFamily: MONO_FONT,
        fontSize: '13px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    hoursStruck: {
        textDecoration: 'line-through',
        color: tokens.colorNeutralForeground3,
    },
    removeButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        background: 'transparent',
        border: 'none',
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        color: tokens.colorNeutralForeground3,
        '&:hover:not(:disabled)': {
            backgroundColor: palette.panelMuted,
            color: tokens.colorNeutralForeground1,
        },
        '&:disabled': {
            cursor: 'not-allowed',
        },
    },
    removeDisabled: {
        visibility: 'hidden',
    },
    removeIcon: {
        width: '14px',
        height: '14px',
    },
});
